<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Thread } from '../services/api';
import { isThreadTaken } from '../utils/thread';

interface Props {
  conversations: Thread[];
  selectedId: string;
  isLoading?: boolean;
  hasMore?: boolean;
  isArchive?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  selectConversation: [conversation: Thread];
  loadMore: [];
  toggleArchive: [];
}>();

const { t } = useI18n();

const scrollContainer = ref<HTMLDivElement | null>(null);

// Two things in every row go stale on their own with no event to announce it: the
// relative timestamp and the take-over highlight, which expires at a time nobody is
// told about. One tick refreshes both, so the file has a single notion of "now"
// instead of a reactive one next to a render-time one that disagree.
const TICK_MS = 30_000;
const nowMs = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | undefined;

const formatTime = (dateStr: string, now: Date): string => {
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMs / 3_600_000);

  if (diffMin < 1) return t('conversations_list.time_just_now');
  if (diffMin < 60) return `${diffMin} min`;
  if (diffH < 24) return `${diffH}h`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString())
    return t('conversations_list.time_yesterday');

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  if (date.getFullYear() === now.getFullYear()) return `${day}.${month}`;
  return `${day}.${month}.${date.getFullYear()}`;
};

/** A row carries at most one pill; only the take-over one pulses. */
interface RowBadge {
  text: string;
  class: string;
  pulsing?: boolean;
}

const BADGE_BASE = 'text-xs px-2 py-1 rounded-full';
// Amber is this list's "a person is needed here" colour; the take-over pill borrows it
// from the badge that already carried that meaning rather than picking a near-miss.
const BADGE_AMBER = `${BADGE_BASE} bg-amber-900/40 text-amber-300`;

const getStatusBadge = (status: Thread['status']): RowBadge | null => {
  switch (status) {
    case 'new':
      return {
        text: t('conversations_list.status_new'),
        class: `${BADGE_BASE} bg-blue-900/40 text-blue-300`,
      };
    case 'needs-support':
      return {
        text: t('conversations_list.status_needs_support'),
        class: BADGE_AMBER,
      };
    default:
      return null;
  }
};

/**
 * Everything a row renders, worked out once. The template asked three separate
 * questions per row before - each re-parsing `takenUntil` - and answering them here
 * keeps the two badges from having to be kept mutually exclusive by hand.
 *
 * A take-over pill replaces the status badge: the row is waiting on a person, which
 * matters more than how the conversation started.
 */
const rows = computed(() => {
  const now = new Date(nowMs.value);

  return props.conversations.map((conversation) => {
    const taken = isThreadTaken(conversation, nowMs.value);

    return {
      conversation,
      taken,
      time: formatTime(conversation.createdAt, now),
      badge: taken
        ? ({
            text: t('conversations_list.status_taken'),
            class: `${BADGE_AMBER} flex items-center gap-1.5`,
            pulsing: true,
          } satisfies RowBadge)
        : getStatusBadge(conversation.status),
    };
  });
});

const handleScroll = () => {
  if (!scrollContainer.value || !props.hasMore || props.isLoading) return;

  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  const threshold = 100;

  if (scrollTop + clientHeight >= scrollHeight - threshold) {
    emit('loadMore');
  }
};

onMounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
  }

  ticker = setInterval(() => {
    nowMs.value = Date.now();
  }, TICK_MS);
});

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll);
  }

  clearInterval(ticker);
});
</script>

<template>
  <div class="h-full bg-[#0A0A0B] border-r border-[#2A2018] flex flex-col">
    <!-- Header -->
    <div class="px-4 md:px-6 py-3 border-b border-[#2A2018]">
      <h1 class="text-lg md:text-xl text-[#F5F0EA] mb-3">
        {{ $t('conversations_list.title') }}
      </h1>
      <div class="flex gap-1 bg-[#111113] rounded-lg p-1">
        <button
          @click="isArchive && emit('toggleArchive')"
          :class="[
            'flex-1 text-xs py-1.5 px-3 rounded-md transition-all',
            !isArchive
              ? 'bg-[#D4956A] text-[#0A0A0B] shadow-[0_0_20px_rgba(212,149,106,0.35)]'
              : 'text-[#524840] hover:text-[#9E9080]',
          ]"
        >
          {{ $t('conversations_list.tab_active') }}
        </button>
        <button
          @click="!isArchive && emit('toggleArchive')"
          :class="[
            'flex-1 text-xs py-1.5 px-3 rounded-md transition-all',
            isArchive
              ? 'bg-[#D4956A] text-[#0A0A0B] shadow-[0_0_20px_rgba(212,149,106,0.35)]'
              : 'text-[#524840] hover:text-[#9E9080]',
          ]"
        >
          {{ $t('conversations_list.tab_archive') }}
        </button>
      </div>
    </div>

    <!-- Conversations List -->
    <div ref="scrollContainer" class="flex-1 overflow-y-auto">
      <button
        v-for="row in rows"
        :key="row.conversation.id"
        @click="emit('selectConversation', row.conversation)"
        :title="row.taken ? $t('conversations_list.status_taken_hint') : undefined"
        :class="[
          'w-full px-4 md:px-6 py-3 md:py-4 text-left transition border-b border-[#2A2018]',
          selectedId === row.conversation.id
            ? 'bg-[#111113] hover:bg-[#111113]'
            : 'hover:bg-[#111113]',
          row.taken && 'taken-over',
        ]"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <p class="text-sm text-[#F5F0EA] truncate">{{ row.conversation.name }}</p>
            <p v-if="row.conversation.lastMessage" class="text-xs text-[#524840] mt-1 truncate">
              {{ row.conversation.lastMessage }}
            </p>
          </div>
          <div class="flex flex-col items-end gap-1 flex-shrink-0">
            <span class="text-xs text-[#9E9080]">{{ row.time }}</span>
            <span v-if="row.badge" :class="row.badge.class">
              <span
                v-if="row.badge.pulsing"
                class="taken-over-dot w-1.5 h-1.5 rounded-full bg-amber-400"
              />
              {{ row.badge.text }}
            </span>
          </div>
        </div>
      </button>

      <!-- Loading Indicator -->
      <div v-if="isLoading" class="px-4 md:px-6 py-4 text-center">
        <div
          class="inline-block w-6 h-6 border-2 border-[#2A2018] border-t-[#F5F0EA] rounded-full animate-spin"
        ></div>
      </div>

      <!-- No More Items -->
      <div
        v-if="!hasMore && conversations.length > 0"
        class="px-4 md:px-6 py-4 text-center text-xs text-[#9E9080]"
      >
        {{ $t('conversations_list.no_more') }}
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && conversations.length === 0" class="px-4 md:px-6 py-8 text-center">
        <p class="text-sm text-[#524840]">{{ $t('conversations_list.empty') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The pulse rides on an overlay rather than on the row itself: animating box-shadow
   repaints a blurred halo every frame, while an opacity tween on a shadow that was
   rasterised once stays on the compositor. The row keeps its own background and hover. */
.taken-over {
  position: relative;
  background-image: linear-gradient(90deg, rgba(245, 158, 11, 0.1), transparent 55%);
}

.taken-over::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.55;
  box-shadow:
    inset 2px 0 0 rgba(245, 158, 11, 1),
    0 0 26px rgba(245, 158, 11, 0.22);
  animation: taken-over-glow 1.3s ease-in-out infinite alternate;
}

/* Only the destination is spelled out - the resting state lives on the element, so it
   is written once and survives `animation: none`. */
@keyframes taken-over-glow {
  to {
    opacity: 1;
  }
}

.taken-over-dot {
  opacity: 0.55;
  transform: scale(0.85);
  animation: taken-over-pulse 1.3s ease-in-out infinite alternate;
}

@keyframes taken-over-pulse {
  to {
    opacity: 1;
    transform: scale(1.15);
  }
}

/* Someone who asked the system for less motion still gets the accent, just still. */
@media (prefers-reduced-motion: reduce) {
  .taken-over::after,
  .taken-over-dot {
    animation: none;
  }
}
</style>
